import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Mail, Send, Trash2, ArrowLeft, Inbox, MessageSquare, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  subject: string | null;
  content: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  sender_name?: string;
  recipient_name?: string;
}

interface MailboxProps {
  onClose: () => void;
  recipientId?: string;
  recipientName?: string;
}

export const Mailbox = ({ onClose, recipientId, recipientName }: MailboxProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newContent, setNewContent] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [view, setView] = useState<'inbox' | 'sent'>('inbox');
  const [allUsers, setAllUsers] = useState<Array<{ user_id: string; display_name: string }>>([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState<string>('');

  useEffect(() => {
    getCurrentUser();
    loadAllUsers();
    if (recipientId && recipientName) {
      setShowCompose(true);
      setSelectedRecipientId(recipientId);
    }
  }, [recipientId, recipientName]);

  useEffect(() => {
    if (user) {
      loadMessages();
    }
  }, [user, view]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const loadAllUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .order('display_name', { ascending: true });

      if (error) throw error;
      setAllUsers(data || []);
    } catch (error: any) {
      console.error('Error loading users:', error);
    }
  };

  const loadMessages = async () => {
    if (!user) return;

    try {
      const column = view === 'inbox' ? 'recipient_id' : 'sender_id';
      const { data, error } = await supabase
        .from('private_messages')
        .select('*')
        .eq(column, user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch profiles for sender/recipient names
      if (data) {
        const userIds = [...new Set(data.map(m => view === 'inbox' ? m.sender_id : m.recipient_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', userIds);

        const messagesWithNames = data.map(msg => ({
          ...msg,
          sender_name: view === 'inbox' 
            ? profiles?.find(p => p.user_id === msg.sender_id)?.display_name || 'Anonymous'
            : 'You',
          recipient_name: view === 'sent'
            ? profiles?.find(p => p.user_id === msg.recipient_id)?.display_name || 'Anonymous'
            : 'You'
        }));

        setMessages(messagesWithNames);
        setUnreadCount(messagesWithNames.filter(m => !m.is_read && m.recipient_id === user.id).length);
      }
    } catch (error: any) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newContent.trim()) {
      toast.error('Please enter a message');
      return;
    }

    const targetRecipientId = recipientId || selectedRecipientId || (replyToId ? selectedMessage?.sender_id : null);
    
    if (!targetRecipientId) {
      toast.error('Please select a recipient');
      return;
    }

    try {

      const { error } = await supabase
        .from('private_messages')
        .insert({
          sender_id: user.id,
          recipient_id: targetRecipientId,
          subject: newSubject || (replyToId ? `Re: ${selectedMessage?.subject || 'No subject'}` : null),
          content: newContent
        });

      if (error) throw error;

      toast.success('Message sent successfully!');
      setNewSubject('');
      setNewContent('');
      setSelectedRecipientId('');
      setShowCompose(false);
      setReplyToId(null);
      loadMessages();
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('private_messages')
        .update({ 
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', messageId);

      if (error) throw error;
      loadMessages();
    } catch (error: any) {
      console.error('Error marking message as read:', error);
    }
  };

  const deleteMessage = async (messageId: string, deleteForBoth: boolean = false) => {
    const message = deleteForBoth 
      ? 'Delete this message for both you and the recipient?' 
      : 'Delete this message for yourself only?';
    
    if (!confirm(message)) return;

    try {
      if (deleteForBoth) {
        // Permanently delete for both
        const { error } = await supabase
          .from('private_messages')
          .delete()
          .eq('id', messageId);

        if (error) throw error;
        toast.success('Message deleted for both parties');
      } else {
        // Soft delete - only hide for current user
        const isMessageSender = selectedMessage?.sender_id === user.id;
        const { error } = await supabase
          .from('private_messages')
          .update({
            [isMessageSender ? 'deleted_by_sender' : 'deleted_by_recipient']: true
          } as any)
          .eq('id', messageId);

        if (error) throw error;
        toast.success('Message deleted');
      }

      setSelectedMessage(null);
      loadMessages();
    } catch (error: any) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  const handleReply = (message: Message) => {
    setReplyToId(message.id);
    setNewSubject(`Re: ${message.subject || 'No subject'}`);
    setShowCompose(true);
  };

  if (loading) {
    return <div className="text-center py-8">Loading messages...</div>;
  }

  if (showCompose) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold">
            {replyToId ? 'Reply to Message' : `New Message${recipientName ? ` to ${recipientName}` : ''}`}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => {
            setShowCompose(false);
            setReplyToId(null);
            setNewSubject('');
            setNewContent('');
            setSelectedRecipientId('');
          }}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            {!recipientId && !replyToId && (
              <div>
                <label className="text-sm font-medium mb-2 block">To</label>
                <select
                  value={selectedRecipientId}
                  onChange={(e) => setSelectedRecipientId(e.target.value)}
                  className="w-full bg-background text-foreground border border-border rounded-lg p-2"
                >
                  <option value="">Select a recipient...</option>
                  {allUsers.filter(u => u.user_id !== user?.id).map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.display_name || 'Anonymous'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-2 block">Subject (optional)</label>
              <Input
                placeholder="Enter subject..."
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Message</label>
              <Textarea
                placeholder="Write your message..."
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                className="min-h-[200px]"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={sendMessage} className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                Send Message
              </Button>
              <Button variant="outline" onClick={() => {
                setShowCompose(false);
                setReplyToId(null);
                setNewSubject('');
                setNewContent('');
                setSelectedRecipientId('');
              }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (selectedMessage) {
    const isMessageSender = selectedMessage.sender_id === user.id;
    const showReadReceipt = isMessageSender && selectedMessage.read_at;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setSelectedMessage(null)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Messages
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleReply(selectedMessage)}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Reply
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => deleteMessage(selectedMessage.id, false)}>
                  Delete for me
                </DropdownMenuItem>
                {isMessageSender && (
                  <DropdownMenuItem 
                    onClick={() => deleteMessage(selectedMessage.id, true)}
                    className="text-destructive"
                  >
                    Delete for both
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">{selectedMessage.subject || 'No subject'}</CardTitle>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>From: {selectedMessage.sender_name}</span>
                <span>{format(new Date(selectedMessage.created_at), 'MMM d, yyyy h:mm a')}</span>
              </div>
              {showReadReceipt && (
                <div className="text-xs text-primary">
                  Read {format(new Date(selectedMessage.read_at), 'MMM d, yyyy h:mm a')}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-foreground whitespace-pre-wrap">{selectedMessage.content}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold">Mailbox</h2>
          {unreadCount > 0 && (
            <span className="bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs">
              {unreadCount} unread
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === 'inbox' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('inbox')}
          >
            <Inbox className="h-4 w-4 mr-2" />
            Inbox
          </Button>
          <Button
            variant={view === 'sent' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('sent')}
          >
            <Send className="h-4 w-4 mr-2" />
            Sent
          </Button>
          <Button onClick={() => setShowCompose(true)}>
            <Mail className="h-4 w-4 mr-2" />
            Compose
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {messages.length === 0 ? (
          <Card className="p-12 text-center">
            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No messages</h3>
            <p className="text-muted-foreground">
              {view === 'inbox' ? 'Your inbox is empty' : 'You haven\'t sent any messages yet'}
            </p>
          </Card>
        ) : (
          messages.map((message) => (
            <Card
              key={message.id}
              className={`cursor-pointer hover:bg-muted/50 transition-colors ${
                !message.is_read && view === 'inbox' ? 'border-primary' : ''
              }`}
              onClick={() => {
                setSelectedMessage(message);
                if (!message.is_read && view === 'inbox') {
                  markAsRead(message.id);
                }
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {!message.is_read && view === 'inbox' && (
                        <span className="w-2 h-2 bg-primary rounded-full"></span>
                      )}
                      <h4 className="font-semibold">
                        {message.subject || 'No subject'}
                      </h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {view === 'inbox' ? `From: ${message.sender_name}` : `To: ${message.recipient_name}`}
                    </p>
                    <p className="text-sm text-foreground line-clamp-2">{message.content}</p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                    {format(new Date(message.created_at), 'MMM d')}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
